/**
 * FIRST OPEN, piece 1: the timing beacon (packet J12).
 *
 * Bobbie, 2026-09-07: "Many people are using the app and the initial upload
 * right now. It takes way way way too long." The signed-OUT boot has been
 * measured from a container against the live hub; the signed-IN boot, which
 * is the one she is describing, cannot be measured that way because a
 * signed-in check writes to real Firebase. So the app has to measure itself
 * and show her the numbers off her own phone.
 *
 * Six points, recorded with performance.mark() and read back on /hub-check
 * (the existing diagnostics surface, where the build stamp and the Firebase
 * and App Check readouts already live). Nothing is sent anywhere: there is
 * no network write in this module and none on the page that reads it.
 *
 * Rules this module keeps, all of them for the same reason (a beacon that
 * breaks a boot is worse than no beacon at all):
 *   - Every call is wrapped. A browser with no Performance API, a locked
 *     down webview, or a thrown mark leaves the app running exactly as it
 *     was, with the missing point simply reading "not recorded".
 *   - Every mark is WRITE ONCE. React StrictMode renders every component
 *     twice in development, and several of these points are taken from a
 *     render body on purpose (see App.tsx), so a second call has to be a
 *     no-op or the second render would overwrite the first one's time.
 *   - Marks are taken in the RENDER body, not in an effect, wherever the
 *     point being timed is "the app now knows the answer". An effect runs
 *     after the commit and after paint, which would fold a frame of
 *     unrelated delay into every number here.
 */

/** The six boot points, in the order they can only ever happen. */
export const BOOT_MARKS = [
  {
    name: 'ldg:module-start',
    label: 'App code started',
  },
  {
    name: 'ldg:first-render',
    label: 'First React render',
  },
  {
    name: 'ldg:auth-answered',
    label: 'Firebase auth answered',
  },
  {
    name: 'ldg:dancer-session-answered',
    label: 'Dancer session answered',
  },
  {
    name: 'ldg:user-doc-answered',
    label: 'Account doc answered',
  },
  {
    name: 'ldg:home-paint',
    label: 'First home paint',
  },
] as const;

export type BootMarkName = (typeof BOOT_MARKS)[number]['name'];

export const BOOT_MARK_NAMES: readonly BootMarkName[] = BOOT_MARKS.map((m) => m.name);

/**
 * Marks already taken in THIS page load. performance.getEntriesByName would
 * answer the same question, but it is a buffer read on every render of the
 * gate component, and this set is what makes "write once" cheap enough to
 * sit in a render body.
 */
const taken = new Set<string>();

function perf(): Performance | null {
  if (typeof performance === 'undefined') return null;
  try {
    return typeof performance.mark === 'function' ? performance : null;
  } catch {
    // Same defensive shape as dancerSessionHint.ts's storage(): some
    // locked-down webviews throw on the ACCESS, not just on the call.
    return null;
  }
}

/**
 * Record a boot point, once. Safe to call from a render body: a repeat call
 * for the same name does nothing at all, so StrictMode's double render and
 * every later re-render leave the first time standing.
 */
export function markBoot(name: BootMarkName): void {
  if (taken.has(name)) return;
  taken.add(name);
  const p = perf();
  if (!p) return;
  try {
    p.mark(name);
  } catch {
    // A full or disabled performance buffer must never break a boot.
  }
}

/** TEST SEAM ONLY. Forget which marks were taken, so a test can run twice. */
export function resetBootMarksForTest(): void {
  taken.clear();
}

export interface BootTimingRow {
  name: BootMarkName;
  label: string;
  /** Milliseconds since the page's time origin, or null when not recorded. */
  ms: number | null;
  /** Milliseconds since the PREVIOUS recorded row, or null when unknowable. */
  sincePreviousMs: number | null;
}

/**
 * Read the boot points back, in fixed order, with the gap to the previous
 * recorded point. A point that never happened (a signed-out visitor has no
 * account doc to answer for) reads as null rather than as a zero, because a
 * zero here would read as "instant" and that is a different claim.
 */
export function readBootTimings(): BootTimingRow[] {
  const p = perf();
  const times = new Map<string, number>();
  if (p && typeof p.getEntriesByName === 'function') {
    for (const mark of BOOT_MARKS) {
      try {
        const entries = p.getEntriesByName(mark.name, 'mark');
        if (entries.length > 0) times.set(mark.name, entries[0].startTime);
      } catch {
        // Leave it unrecorded rather than guessing.
      }
    }
  }
  let previous: number | null = null;
  return BOOT_MARKS.map((mark) => {
    const raw = times.get(mark.name);
    const ms = typeof raw === 'number' ? Math.round(raw) : null;
    const sincePreviousMs = ms !== null && previous !== null ? ms - previous : null;
    if (ms !== null) previous = ms;
    return { name: mark.name, label: mark.label, ms, sincePreviousMs };
  });
}

/**
 * One line per point, for the diagnostics readout. Kept here rather than in
 * the page so a test can pin the wording without mounting React.
 */
export function formatBootTiming(row: BootTimingRow): string {
  if (row.ms === null) return 'not recorded';
  if (row.sincePreviousMs === null) return `${row.ms} ms`;
  return `${row.ms} ms (+${row.sincePreviousMs})`;
}
